import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseRow from '~/components/common/base-row/BaseRow';
import { cast, handleApiError } from '~/utils/helper';
import { BaseScrollbar } from '~/components/common';
import notificationApi from '~/apis/notification';
import { AlertIcon, BellRingingIcon, Alert3Icon, InformationLine3Icon, InformationLineIcon } from '~/assets/icons';
import { AlertLevelEnum, AlertStatusEnum, AlertTypeEnum, TypeAlertEnum } from '~/utils/enum';
import { useAppSelector } from '~/redux/hooks';
import { IGetListNotification } from '~/utils/interface/actionLog';

const Notification = () => {
  const { t } = useTranslation('admin/alert');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const [actionLogs, setActionLogs] = useState<IGetListNotification[]>([]);
  const [filterList, setFilterList] = useState<IGetListNotification[]>([]);

  const setAlertLevel = useRef<number>(0);

  useEffect(() => {
    if (!username) return;

    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const response = await notificationApi.getList({ username: username });
        if (!isCancelled) {
          setActionLogs(response?.data?.result);
          setFilterList(response?.data?.result);
        }
      } catch (error) {
        if (!isCancelled) handleApiError(error);
      } finally {
        if (!isCancelled) {
          timeoutId = setTimeout(poll, 10000); // 10s
        }
      }
    };

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const getAlertType = (alert_type: number): string => {
    if (alert_type === AlertTypeEnum.REMOVE_DEVICE) return t('alert_type_options.' + AlertTypeEnum.REMOVE_DEVICE);
    if (alert_type === AlertTypeEnum.GEOFENCE) return t('alert_type_options.' + AlertTypeEnum.GEOFENCE);
    if (alert_type === AlertTypeEnum.GATHERING) return t('alert_type_options.' + AlertTypeEnum.GATHERING);
    if (alert_type === AlertTypeEnum.OFFLINE) return t('alert_type_options.' + AlertTypeEnum.OFFLINE);
    return '';
  };

  const handlFilterClick = async (level: number) => {
    setAlertLevel.current = level;

    let listFilter: IGetListNotification[] = [];

    if (level === 0) listFilter = actionLogs;
    else if (level === 4)
      listFilter = actionLogs.filter((item: IGetListNotification) => item.type === TypeAlertEnum.ACTION_LOG);
    else listFilter = actionLogs.filter((item: IGetListNotification) => item.raw?.level === level);

    setFilterList(listFilter);
  };

  const renderInformation = (row: IGetListNotification) => {
    return (
      <BaseRow className='border border-[#D7DEE7] !items-center rounded-xl p-2.5 gap-3 hover:opacity-80 mb-2'>
        <Box className='flex-1 flex flex-col items-center justify-center gap-3'>
          <BellRingingIcon className='w-9 h-9' />
          <Typography className='truncate text-sm font-medium text-[#161A1F]'>
            {row.created_at ? dayjs(row.created_at).format('HH:mm DD/MM/YYYY') : ''}
          </Typography>
        </Box>
        <Box className='w-60 m-auto'>
          <Typography className='text-base font-semibold text-[#161A1F] mb-1'>{t('title')}</Typography>
          <Typography className='text-sm text-[#161A1F]'>{cast(row.raw?.description)}</Typography>
        </Box>
      </BaseRow>
    );
  };

  const renderWaring = (row: IGetListNotification) => {
    if (row.raw?.status === AlertStatusEnum.UNPROCESSED) {
      return (
        <BaseRow className='!items-center rounded-xl p-2.5 gap-3 hover:opacity-80 border border-[#E48A1D] bg-[#FEF4E6] mb-2'>
          <Box className='flex-1 flex flex-col items-center justify-center gap-3'>
            <InformationLine3Icon className='w-9 h-9' />
            <Typography className='text-white border bg-[#FF9B21] rounded-full font-bold py-1 px-4'>
              {t('status_options.unProcessed')}
            </Typography>
            <Typography className='truncate text-sm font-medium text-[#404854]'>
              {row.created_at ? dayjs(row.created_at).format('HH:mm DD/MM/YYYY') : ''}
            </Typography>
          </Box>
          <Box className='w-60 m-auto'>
            <Typography className='text-base font-bold text-[#E48A1D]'>{getAlertType(cast(row.raw?.type))}</Typography>
            <Typography className='my-1 text-sm font-bold text-[#161A1F]'>{cast(row.raw?.full_name)}</Typography>
            <Typography className='text-sm font-medium text-[#161A1F]'>{cast(row.raw?.imei)}</Typography>
            <Typography className='mt-1 text-sm font-medium text-[#161A1F]'>{cast(row.raw?.address)}</Typography>
          </Box>
        </BaseRow>
      );
    }

    if (row.raw?.status === AlertStatusEnum.PROCESSED) {
      return (
        <BaseRow className='!items-center rounded-xl p-2.5 gap-3 hover:opacity-80 border border-[#E48A1D1F] mb-2'>
          <Box className='flex-1 flex flex-col items-center justify-center gap-3'>
            <InformationLineIcon className='w-9 h-9' />
            <Typography className='text-[#FF9B21] border-[#FF9B21] border bg-white rounded-full font-bold py-1 px-4'>
              {t('status_options.processed')}
            </Typography>
            <Typography className='truncate text-sm font-medium text-[#404854]'>
              {row.created_at ? dayjs(row.created_at).format('HH:mm DD/MM/YYYY') : ''}
            </Typography>
          </Box>
          <Box className='w-60 m-auto'>
            <Typography className='text-base font-semibold text-[#404854]'>
              {getAlertType(cast(row.raw?.type))}
            </Typography>
            <Typography className='my-1 text-sm font-medium text-[#404854]'>{cast(row.raw?.full_name)}</Typography>
            <Typography className='text-sm font-medium text-[#404854]'>{cast(row.raw?.imei)}</Typography>
            <Typography className='mt-1 text-sm font-medium text-[#404854]'>{cast(row.raw?.address)}</Typography>
          </Box>
        </BaseRow>
      );
    }
  };

  const renderCritical = (row: IGetListNotification) => {
    if (row.raw?.status === AlertStatusEnum.UNPROCESSED) {
      return (
        <BaseRow className='!items-center rounded-xl p-2.5 gap-3 hover:opacity-80 shadow-[0_1px_6px_rgba(0,0,0,0.1)] bg-[#C6282833]/20 mb-2'>
          <Box className='flex-1 flex flex-col items-center justify-center gap-2'>
            <Alert3Icon className='w-9 h-9' />
            <Typography className='text-white bg-[#C62828] rounded-full font-bold py-1 px-4'>
              {t('status_options.unProcessed')}
            </Typography>
            <Typography className='truncate text-sm font-medium text-[#404854]'>
              {row.created_at ? dayjs(row.created_at).format('HH:mm DD/MM/YYYY') : ''}
            </Typography>
          </Box>
          <Box className='w-60 m-auto'>
            <Typography className='text-base font-bold text-[#C62828]'>{getAlertType(cast(row.raw?.type))}</Typography>
            <Typography className='my-1 text-sm font-semibold text-[#161A1F]'>{cast(row.raw?.full_name)}</Typography>
            <Typography className='text-sm font-normal text-[#161A1F]'>{cast(row.raw?.imei)}</Typography>
            <Typography className='mt-1 text-sm font-normal text-[#161A1F]'>{cast(row.raw?.address)}</Typography>
          </Box>
        </BaseRow>
      );
    }

    if (row.raw?.status === AlertStatusEnum.PROCESSED) {
      return (
        <BaseRow className='!items-center rounded-xl p-2.5 gap-3 hover:opacity-80 shadow-[0_1px_6px_rgba(0,0,0,0.1)] border-[#E48A1D1F] mb-2'>
          <Box className='flex-1 flex flex-col items-center justify-center gap-3'>
            <AlertIcon className='w-9 h-9' />
            <Typography className='text-[#C62828] bg-white border-[#C62828] border rounded-full font-bold py-1 px-4'>
              {t('status_options.processed')}
            </Typography>
            <Typography className='truncate text-sm font-medium text-[#404854]'>
              {row.created_at ? dayjs(row.created_at).format('HH:mm DD/MM/YYYY') : ''}
            </Typography>
          </Box>
          <Box className='w-60 m-auto'>
            <Typography className='text-base font-semibold text-[#404854]'>
              {getAlertType(cast(row.raw?.type))}
            </Typography>
            <Typography className='my-1 text-sm font-medium text-[#404854]'>{cast(row.raw?.full_name)}</Typography>
            <Typography className='text-sm font-medium text-[#404854]'>{cast(row.raw?.imei)}</Typography>
            <Typography className='mt-1 text-sm font-medium text-[#404854]'>{cast(row.raw?.address)}</Typography>
          </Box>
        </BaseRow>
      );
    }
  };

  const renderContent = (row: IGetListNotification) => {
    if (row.type === TypeAlertEnum.ACTION_LOG) return renderInformation(row);
    if (row.type === TypeAlertEnum.ALERT && row.raw?.level === AlertLevelEnum.WARNING) return renderWaring(row);
    if (row.type === TypeAlertEnum.ALERT && row.raw?.level === AlertLevelEnum.CRITICAL) return renderCritical(row);

    return null;
  };

  return (
    <Box className='w-full'>
      <Typography className='text-[#0E2F58] text-lg font-semibold p-4'>
        {t('alert_level_options.notification')}
      </Typography>
      <Box className='p-1.5'>
        <BaseRow className='gap-2 justify-between px-1.5 mb-3'>
          <Box
            className={`bg-[#0E2F58] border rounded-full py-1.5 px-6 text-sm hover:cursor-pointer hover:opacity-80 ${
              setAlertLevel.current === 0 ? 'bg-[#005F73] text-white' : 'bg-white text-[#161A1F]'
            }`}
            onClick={() => handlFilterClick(0)}
          >
            {t('alert_level_options.all')}
          </Box>
          <Box
            className={`text-[#C62828] border border-[#C62828] rounded-full py-1.5 px-2 text-sm hover:cursor-pointer hover:opacity-80 ${
              setAlertLevel.current === 3 ? 'bg-[#C62828] text-white' : 'bg-white'
            }`}
            onClick={() => handlFilterClick(3)}
          >
            {t('alert_level_options.critical')}
          </Box>
          <Box
            className={`text-[#E48A1D] border border-[#E48A1D] rounded-full hover:cursor-pointer py-1.5 px-2 text-sm hover:opacity-80 ${
              setAlertLevel.current === 2 ? 'bg-[#FF9500] text-white' : 'bg-white'
            }`}
            onClick={() => handlFilterClick(2)}
          >
            {t('alert_level_options.warning')}
          </Box>
          <Box
            className={`text-[#404854] border border-[#404854] rounded-full py-1.5 px-2 text-sm hover:cursor-pointer hover:opacity-80 ${
              setAlertLevel.current === 4 ? 'bg-[#000000] text-white' : 'bg-white'
            }`}
            onClick={() => handlFilterClick(4)}
          >
            {t('alert_level_options.notification')}
          </Box>
        </BaseRow>
        {filterList?.length > 0 && (
          <BaseScrollbar maxHeight='600px'>
            <Box className='overflow-hidden'>
              <Table className='w-[98%]'>
                <TableBody>
                  {filterList.map((row: IGetListNotification, index: number) => {
                    return (
                      <TableRow key={`${row.id}-${index}`}>
                        <TableCell className='border-none hover:cursor-pointer py-0'>{renderContent(row)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        )}
      </Box>
    </Box>
  );
};

export default memo(Notification);
